import React, { useState, useEffect, useRef } from 'react';
import ExpandableContent from './ExpandableContent';

const CountUpAnimation: React.FC<{
  end: number;
  duration?: number;
  suffix?: string;
  isVisible: boolean;
}> = ({ end, duration = 2000, suffix = '', isVisible }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    // Fallback: démarrer l'animation après 500ms même si pas visible
    const fallbackTimer = setTimeout(() => {
      if (!hasAnimated) {
        setHasAnimated(true);
        startAnimation();
      }
    }, 500);

    if (isVisible && !hasAnimated) {
      clearTimeout(fallbackTimer);
      setHasAnimated(true);
      startAnimation();
    }

    function startAnimation() {
      let startTime: number;
      let animationFrame: number;

      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);

        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentCount = Math.floor(easeOutQuart * end);
        setCount(currentCount);

        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        } else {
          setCount(end); // S'assurer que la valeur finale est exacte
        }
      };

      animationFrame = requestAnimationFrame(animate);
      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }

    return () => {
      clearTimeout(fallbackTimer);
    };
  }, [isVisible, hasAnimated, end, duration]);

  return <span>{count}{suffix}</span>;
};

const Historique: React.FC = () => {
  const [isStatsVisible, setIsStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Démarrer l'animation après un délai
    const timer = setTimeout(() => {
      setIsStatsVisible(true);
    }, 2000);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsStatsVisible(true);
        }
      },
      { threshold: 0.1 } // Seuil plus bas
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, []);
  const timelineEvents = [
    {
      year: '1984',
      date: '08 octobre 1984',
      title: 'Fondation de l\'ENTMCH',
      description: 'Création de l\'École Nationale de Technologie Médicale du Cap-Haïtien par le Dr Robert Germain (Ministre de la Santé) et Macajou A. Médard (Chef de Laboratoire HUJ)',
      details: [
        'Première promotion : 10 étudiants',
        'Formation : 2 ans',
        'Direction : 2 personnes (directeur + secrétaire)',
        '5 professeurs nommés + 4 bénévoles',
        'Local : petite bâtisse à l\'HUJ (1 salle de cours)'
      ],
      color: 'blue',
      icon: '🏛️'
    },
    {
      year: '1984-1988',
      title: 'Premières années',
      description: 'Période de développement initial avec les premières promotions',
      details: [
        '1ère promotion graduée : 8 étudiants',
        '2ème promotion graduée : 9 étudiants',
        'Admission tous les 2 ans (manque d\'espace)',
        'Formation de 2 ans'
      ],
      color: 'green',
      icon: '🎓'
    },
    {
      year: '1988',
      title: 'Fermeture de l\'École',
      description: 'Décision de fermeture par les nouveaux responsables du Ministère de la Santé',
      details: [
        'Fermeture décidée par le nouveau Ministre',
        'Complicité du Directeur Région Nord',
        'Début de 10 années de silence'
      ],
      color: 'red',
      icon: '🚫'
    },
    {
      year: '1988-1998',
      title: 'Période de fermeture',
      description: '10 années de silence - L\'école reste fermée',
      details: [
        'Aucune activité pédagogique',
        'Perte d\'une décennie de formation',
        'Impact sur la technologie médicale en Haïti'
      ],
      color: 'gray',
      icon: '⏸️'
    },
    {
      year: '1998',
      title: 'Réouverture historique',
      description: 'Le Président René Préval ordonne la réouverture de l\'ENTMCH',
      details: [
        'Intervention énergique du Président Préval',
        'Reprise des activités normales',
        'Formation étendue de 2 à 3 ans',
        'Ajout d\'un Directeur Adjoint'
      ],
      color: 'green',
      icon: '🔄'
    },
    {
      year: '1998-2011',
      title: 'Période de croissance',
      description: 'Développement et reconnaissance de l\'excellence',
      details: [
        '~400 candidats/an à l\'examen d\'admission',
        '~50 étudiants admis/an (capacité limitée)',
        'Collaboration avec École des Infirmières',
        'Projet EXECO S.A pour nouveau bâtiment',
        'Partenariat I-TECH Haïti'
      ],
      color: 'purple',
      icon: '📈'
    },
    {
      year: '2011',
      date: '27 janvier 2011',
      title: 'Élévation au rang d\'Institut',
      description: 'Arrêté Présidentiel - Transformation en Institut Supérieur',
      details: [
        'Publication Moniteur : 4 février 2011, No 15',
        'Nouveau nom : ISTMECH',
        'Reconnaissance de la qualité de formation',
        'Performance exceptionnelle des diplômés'
      ],
      color: 'gold',
      icon: '👑'
    }
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-500 border-blue-500 text-blue-600',
      green: 'bg-green-500 border-green-500 text-green-600',
      red: 'bg-red-500 border-red-500 text-red-600',
      gray: 'bg-gray-500 border-gray-500 text-gray-600',
      purple: 'bg-purple-500 border-purple-500 text-purple-600',
      gold: 'bg-yellow-500 border-yellow-500 text-yellow-600'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <section
      id="historique"
      className="py-16 bg-gray-50 dark:bg-gray-900 transition-colors duration-200 scroll-mt-24"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Historique de l'ISTMECH
          </h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-8"></div>

          <div className="mt-6 text-left text-gray-600 dark:text-gray-300 max-w-3xl mx-auto space-y-4 leading-relaxed">
            <p>
              L'Institut Supérieur de Technologie Médicale du Cap-Haïtien (ISTMECH) a été fondé le 08 octobre 1984
              par le docteur Robert Germain, Ministre de la Santé Publique et de la Population, et le biologiste
              Macajou A. Médard, Chef de Laboratoire à l'Hôpital Universitaire Justinien du Cap-Haïtien (HUJ). Il
              portait alors le nom d'<strong>École Nationale de Technologie Médicale du Cap-Haïtien (ENTMCH)</strong>.
            </p>

            <p>
              La durée des études était de deux ans et la première promotion comptait dix étudiants. La direction
              était assurée par deux personnes : le directeur et une secrétaire de direction. Cinq professeurs nommés
              par le Ministère de la Santé Publique assuraient une partie des cours, l'autre partie revenant à quatre
              professeurs bénévoles.
            </p>

            <p>
              L'École occupait une petite bâtisse située à l'Hôpital Universitaire Justinien, comprenant au
              rez-de-chaussée une unique salle de cours et, à l'étage, la direction et le secrétariat. Ne disposant que
              d'une seule salle, l'ENTMCH ne pouvait admettre une nouvelle promotion que tous les deux ans. Plus tard,
              une deuxième salle fut mise à sa disposition par l'École des Infirmières Notre Dame de la Sagesse,
              également logée à l'HUJ, permettant ainsi l'admission d'une nouvelle promotion chaque année.
            </p>

            <p>
              De 1984 à 1988, l'École a gradué deux promotions : la première de huit étudiants et la deuxième de neuf,
              la durée de la formation restant de deux ans. En 1988, les nouveaux responsables du Ministère de la Santé
              Publique décidèrent de fermer l'École, avec la complicité de la Direction de la Région Nord. L'École resta
              ainsi fermée pendant dix ans, de 1988 à 1998.
            </p>

            <p>
              En 1998, l'ENTMCH reprit ses activités grâce à l'intervention énergique du président René Préval, qui
              ordonna sa réouverture. Depuis, l'École fonctionne normalement et la formation est passée de deux à trois
              ans. Toutefois, elle fait face aux mêmes difficultés : manque de moyens, d'espace, de professeurs
              (dont un grand nombre de bénévoles) et absence de local adéquat répondant aux normes, malgré la qualité
              reconnue de la formation.
            </p>

            <p>
              Chaque année, environ quatre cents candidats se présentent à l'examen d'admission, mais l'École ne peut
              en accueillir qu'une cinquantaine, et encore difficilement. À ce jour, elle a gradué quatorze promotions
              comptant au total <strong>224 diplômés</strong>.
            </p>

            <p>
              La firme nationale <strong>EXECO S.A</strong> a gracieusement produit un projet de construction pour l'École.
              Ce projet est en possession du Ministère de la Santé Publique et de plusieurs bailleurs de fonds et reste
              en attente d'une réponse favorable.
            </p>

            <p>
              Depuis le 27 janvier 2011, par Arrêté Présidentiel paru dans le Moniteur du vendredi 4 février 2011,
              No&nbsp;15, l'École a été élevée au rang d'<strong>Institut Supérieur</strong> et a pris le nom d'Institut
              Supérieur de Technologie Médicale du Cap-Haïtien – <strong>ISTMECH</strong>. Elle doit cette promotion à la
              qualité de la formation dispensée et à la performance de ses diplômés.
            </p>
          </div>
        </div>

        {/* Statistiques actuelles */}
        <div ref={statsRef} className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center shadow-lg transform hover:scale-105 transition-all duration-300">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
              <CountUpAnimation end={224} isVisible={isStatsVisible} duration={2000} />
            </div>
            <div className="text-gray-600 dark:text-gray-300">Diplômés à ce jour</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center shadow-lg transform hover:scale-105 transition-all duration-300">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
              <CountUpAnimation end={14} isVisible={isStatsVisible} duration={1200} />
            </div>
            <div className="text-gray-600 dark:text-gray-300">Promotions graduées</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center shadow-lg transform hover:scale-105 transition-all duration-300">
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
              <CountUpAnimation end={400} isVisible={isStatsVisible} duration={2500} />
            </div>
            <div className="text-gray-600 dark:text-gray-300">Candidats/an</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center shadow-lg transform hover:scale-105 transition-all duration-300">
            <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mb-2">
              <CountUpAnimation end={3} isVisible={isStatsVisible} duration={1000} />
            </div>
            <div className="text-gray-600 dark:text-gray-300">Années de formation</div>
          </div>
        </div>

        {/* Timeline avec contenu expandable */}
        <ExpandableContent
          buttonColor="blue"
          previewContent={
            <div className="space-y-8">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Chronologie Principale
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Les moments clés de l'histoire de l'ISTMECH
                </p>
              </div>

              {/* Timeline réduite - 3 premiers événements */}
              <div className="relative">
                <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 w-1 bg-gray-300 h-full"></div>
                {timelineEvents.slice(0, 3).map((event, index) => (
                  <div key={index} className={`relative flex items-center mb-12 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`absolute left-8 md:left-1/2 transform md:-translate-x-1/2 w-6 h-6 rounded-full border-4 ${getColorClasses(event.color).split(' ')[1]} bg-white z-10`}></div>
                    <div className={`ml-20 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                        <div className="flex items-center mb-4">
                          <span className="text-2xl mr-3">{event.icon}</span>
                          <div>
                            <div className={`text-2xl font-bold ${getColorClasses(event.color).split(' ')[2]} dark:text-gray-100`}>
                              {event.year}
                            </div>
                            {event.date && (
                              <div className="text-sm text-gray-500 dark:text-gray-400">{event.date}</div>
                            )}
                          </div>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                          {event.title}
                        </h3>
                        <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                          {event.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          }
        >
          {/* Timeline complète */}
          <div className="relative">
            <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 w-1 bg-gray-300 h-full"></div>
            {timelineEvents.map((event, index) => (
              <div key={index} className={`relative flex items-center mb-12 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                {/* Point sur la timeline */}
                <div className={`absolute left-8 md:left-1/2 transform md:-translate-x-1/2 w-6 h-6 rounded-full border-4 ${getColorClasses(event.color).split(' ')[1]} bg-white z-10`}></div>

                {/* Contenu */}
                <div className={`ml-20 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                    {/* En-tête de l'événement */}
                    <div className="flex items-center mb-4">
                      <span className="text-2xl mr-3">{event.icon}</span>
                      <div>
                        <div className={`text-2xl font-bold ${getColorClasses(event.color).split(' ')[2]} dark:text-gray-100`}>
                          {event.year}
                        </div>
                        {event.date && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">{event.date}</div>
                        )}
                      </div>
                    </div>

                    {/* Titre et description */}
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      {event.title}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                      {event.description}
                    </p>

                    {/* Détails */}
                    {event.details && (
                      <ul className="space-y-2">
                        {event.details.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-start">
                            <span className={`w-2 h-2 rounded-full ${getColorClasses(event.color).split(' ')[0]} mt-2 mr-3 flex-shrink-0`}></span>
                            <span className="text-gray-600 dark:text-gray-200 text-sm">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Section défis actuels */}
          <div className="mt-16 bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg transition-colors duration-200">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              Défis et Réalisations
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Défis */}
              <div>
                <h4 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4 flex items-center">
                  <span className="mr-2">⚠️</span>
                  Défis actuels
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-gray-700 dark:text-gray-300">Manque de local adéquat répondant aux normes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-gray-700 dark:text-gray-300">Aucune source de revenu ni allocation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-gray-700 dark:text-gray-300">Manque de professeurs (nombreux bénévoles)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-gray-700 dark:text-gray-300">Capacité limitée (50 admis sur 400 candidats)</span>
                  </li>
                </ul>
              </div>

              {/* Réalisations */}
              <div>
                <h4 className="text-lg font-semibold text-green-600 dark:text-green-400 mb-4 flex items-center">
                  <span className="mr-2">✅</span>
                  Réalisations remarquables
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-gray-700 dark:text-gray-300">Meilleure École de Technologie Médicale du pays</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-gray-700 dark:text-gray-300">224 diplômés formés avec excellence</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-gray-700 dark:text-gray-300">Projet architectural EXECO S.A disponible</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-gray-700 dark:text-gray-300">Reconnaissance internationale et élévation en Institut</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Note sur le projet de construction */}
            <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-400 p-6">
              <h5 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Projet de Construction</h5>
              <p className="text-blue-800 dark:text-blue-200">
                La firme nationale <strong>EXECO S.A</strong> a gracieusement produit un projet de construction
                pour l'École. Ce projet est en possession du Ministère de la Santé Publique et de plusieurs
                bailleurs de fonds, en attente d'une réponse favorable.
              </p>
            </div>
          </div>
        </ExpandableContent>
      </div>
    </section>
  );
};

export default Historique;
